// src/context/SiteContext.js
// ─────────────────────────────────────────────────────────────────────────────
// Loads profile, about, skills, socials, projects, certificates, and experience
// once at startup and exposes them to every component via useSite().
// Falls back to local JSON if Firestore is unreachable.
// ─────────────────────────────────────────────────────────────────────────────
import React, { createContext, useContext, useEffect, useState } from "react";
import {
  getProfile,
  getAbout,
  getSkills,
  getSocials,
  getProjects,
  getCertificates,
  getExperience,
} from "../firebase/dataService";
import fallback from "../data/content.json";

const SiteContext = createContext(null);

export const SiteProvider = ({ children }) => {
  const [profile, setProfile] = useState(fallback.profile || {});
  const [about, setAbout] = useState(fallback.about || {});
  const [skills, setSkills] = useState(fallback.skills || []);
  const [socials, setSocials] = useState(fallback.socials || []);
  const [projects, setProjects] = useState(fallback.projects || []);
  const [certificates, setCertificates] = useState(fallback.certificates || []);
  const [experience, setExperience] = useState(fallback.resume?.experience || []);
  const [loading, setLoading] = useState(true);

  // Reload helper — used by admin after saving
  const reload = async () => {
    try {
      const [p, a, sk, so, pr, certs, exp] = await Promise.all([
        getProfile(),
        getAbout(),
        getSkills(),
        getSocials(),
        getProjects(),
        getCertificates(),
        getExperience(),
      ]);

      setProfile(p || fallback.profile || {});
      setAbout(a || fallback.about || {});
      setSkills(sk || fallback.skills || []);
      setSocials(so || fallback.socials || []);
      setProjects(pr || fallback.projects || []);
      setCertificates(certs || fallback.certificates || []);
      setExperience(exp || fallback.resume?.experience || []);
    } catch (e) {
      console.warn("SiteContext: falling back to local JSON", e.message);

      setProfile(fallback.profile || {});
      setAbout(fallback.about || {});
      setSkills(fallback.skills || []);
      setSocials(fallback.socials || []);
      setProjects(fallback.projects || []);
      setCertificates(fallback.certificates || []);
      setExperience(fallback.resume?.experience || []);
    }
  };

  useEffect(() => {
    reload().finally(() => setLoading(false));
  }, []);

  return (
    <SiteContext.Provider
      value={{
        profile,
        about,
        skills,
        socials,
        projects,
        certificates,
        experience,
        loading,
        reload,
      }}
    >
      {children}
    </SiteContext.Provider>
  );
};

export const useSite = () => {
  const ctx = useContext(SiteContext);
  if (!ctx) throw new Error("useSite must be used inside <SiteProvider>");
  return ctx;
};